import firebaseStorage from "@/utility/firebaseStorage"

import {
    ref as firebaseRef,
    onValue,
    set,
    remove,
    onDisconnect,
    off,
    serverTimestamp,
} from 'firebase/database'

// max age before a ring entry is treated as a zombie. caller's hard
// timeout is 30s, so anything older than 35s should not exist under
// any normal flow. defends against the rare case where the caller's
// onDisconnect failed to fire and the entry got stuck.
const STALE_RING_MS = 35000

// shared, non-serializable Firebase handles for the caller-side write.
// kept outside vuex state so we can cancel onDisconnect on explicit
// cancel/missed before the entry is removed.
const ringWriteStorage = {
    ref: null,
    onDisconnect: null,
}

const getStorageKey = (userId) => `user-${userId}`

const ringPathForUser = (userId) =>
    `globalCallRing/${process.env.NODE_ENV}/${getStorageKey(userId)}`

const state = () => ({
    enabled: false,
    incomingCallId: null,
})

const mutations = {
    setEnabled(state, value) {
        state.enabled = value
    },
    setIncomingCallId(state, value) {
        state.incomingCallId = value
    },
}

const actions = {
    enable({ state, commit, rootState, dispatch }) {
        if (state.enabled) {
            return
        }

        if (!rootState.onboarding?.user?.id) {
            return
        }

        commit('setEnabled', true)

        const dbRef = firebaseStorage.db
        const path = ringPathForUser(rootState.onboarding.user.id)
        firebaseStorage.ringRef = firebaseRef(dbRef, path)

        onValue(firebaseStorage.ringRef, (snapshot) => {
            const data = snapshot.val()

            if (!data) {
                if (state.incomingCallId) {
                    dispatch('call/handleRingRemoved', null, { root: true })
                    commit('setIncomingCallId', null)
                }
                return
            }

            const startedAt = typeof data.started === 'number' ? data.started : 0
            const ageMs = Date.now() - startedAt

            if (!startedAt || ageMs > STALE_RING_MS) {
                remove(firebaseStorage.ringRef)
                return
            }

            if (state.incomingCallId === data.callId) {
                return
            }

            commit('setIncomingCallId', data.callId)
            dispatch('call/openCall', { id: data.callId }, { root: true })
        })
    },

    disable({ state, commit }) {
        if (!state.enabled || !firebaseStorage.ringRef) {
            commit('setEnabled', false)
            commit('setIncomingCallId', null)
            return
        }

        off(firebaseStorage.ringRef)
        firebaseStorage.ringRef = null

        commit('setEnabled', false)
        commit('setIncomingCallId', null)
    },

    // called by caller-side call/create after the API returns
    async writeRing(_, { call, fromUserId }) {
        const dbRef = firebaseStorage.db
        const path = ringPathForUser(call.toUser.id)
        const ringRef = firebaseRef(dbRef, path)

        ringWriteStorage.ref = ringRef
        ringWriteStorage.onDisconnect = onDisconnect(ringRef)
        await ringWriteStorage.onDisconnect.remove()

        await set(ringRef, {
            callId: call.id,
            fromUserId,
            started: serverTimestamp(),
        })
    },

    // called by caller-side cancel/missed before hitting the backend
    async clearRing() {
        if (!ringWriteStorage.ref) {
            return
        }

        try {
            await ringWriteStorage.onDisconnect?.cancel()
        } catch (e) {
            // ignore — onDisconnect may already be invalidated
        }

        try {
            await remove(ringWriteStorage.ref)
        } catch (e) {
            // ignore — entry may already be gone
        }

        ringWriteStorage.ref = null
        ringWriteStorage.onDisconnect = null
    },

    // called by callee-side answer/decline so the ringtone stops
    // instantly on this device, regardless of backend round-trip.
    // clear incomingCallId BEFORE the firebase remove so the onValue
    // null branch sees no incomingCallId and does not fire
    // handleRingRemoved (which would close the modal mid-answer).
    async clearIncomingRing({ commit, rootState }) {
        commit('setIncomingCallId', null)

        if (!rootState.onboarding?.user?.id) {
            return
        }

        const dbRef = firebaseStorage.db
        const path = ringPathForUser(rootState.onboarding.user.id)

        try {
            await remove(firebaseRef(dbRef, path))
        } catch (e) {
            // ignore
        }
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
}
